import classNames from "classnames";
import React, { Fragment, FunctionComponent, useMemo } from "react";
import { Link } from "../link";
import { Separator } from "../separator/separator";
import { LightDarkVariant } from "../types";
import {
  GlobalHeaderUtilityHeaderIconLinkProps,
  GlobalHeaderUtilityHeaderLinkProps,
  GlobalHeaderUtilityHeaderLinksAlignment,
} from "./global-header";
import { CSS_CLASSES } from "./global-header-css-classes";

export interface GlobalHeaderUtilityBarProps {
  variant: LightDarkVariant;
  links?: GlobalHeaderUtilityHeaderLinkProps[];
  iconLinks?: GlobalHeaderUtilityHeaderIconLinkProps[];
  linksAlignment?: GlobalHeaderUtilityHeaderLinksAlignment;
  isUserSignedIn: boolean;
  constrainContentWidth?: boolean;
  isDesktop: boolean;
}

export const GlobalHeaderUtilityBarRenderer: FunctionComponent<GlobalHeaderUtilityBarProps> = ({
  variant,
  links = [],
  iconLinks = [],
  linksAlignment,
  isUserSignedIn,
  constrainContentWidth,
  isDesktop,
}) => {
  const linksToRender = useMemo(() => {
    return links.filter((l) => !isUserSignedIn || !l.hideWhenSignedIn);
  }, [links, isUserSignedIn]);

  const hasLinks = useMemo(() => isDesktop && linksToRender.length > 0, [isDesktop, linksToRender.length]);

  const iconLinksToRender = useMemo(() => {
    const filteredIconLinks = iconLinks.filter((i) => !isUserSignedIn || !i.hideWhenSignedIn);

    if (isDesktop) {
      return filteredIconLinks;
    }

    const iconLinksToShowOnMobile = filteredIconLinks.filter((i) => i.placementOnMobile === "utility-header");
    return iconLinksToShowOnMobile.length > 2
      ? iconLinksToShowOnMobile.slice(iconLinksToShowOnMobile.length - 2)
      : iconLinksToShowOnMobile;
  }, [iconLinks, isDesktop, isUserSignedIn]);

  const hasIconLinks = useMemo(() => iconLinksToRender.length > 0, [iconLinksToRender.length]);

  const rootClassName = useMemo(
    () =>
      classNames(CSS_CLASSES.GLOBAL_HEADER_UTILITY_ROOT, {
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_LIGHT]: variant === "light",
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_DARK]: variant === "dark",
      }),
    [variant]
  );

  const contentClassName = useMemo(
    () =>
      classNames(CSS_CLASSES.GLOBAL_HEADER_UTILITY_CONTENT, {
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_CONTENT_ALIGN_LEFT]: linksAlignment === "left",
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_CONTENT_ALIGN_CENTER]: linksAlignment === "center",
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_CONTENT_ALIGN_RIGHT]: linksAlignment === "right",
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_CONTENT_ALIGN_SPACE_BETWEEN]: linksAlignment === "space-between",
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_CONTENT_ALIGN_SPACE_AROUND]: linksAlignment === "space-around",
        [CSS_CLASSES.GLOBAL_HEADER_UTILITY_CONTENT_ALIGN_SPACE_EVENLY]: linksAlignment === "space-evenly",
        "container mx-auto": constrainContentWidth,
      }),
    [constrainContentWidth, linksAlignment]
  );

  return hasLinks || hasIconLinks ? (
    <div className={rootClassName}>
      <div className={contentClassName}>
        {hasLinks && (
          <div className={CSS_CLASSES.GLOBAL_HEADER_UTILITY_LINKS}>
            {linksToRender.map((linkProps, i) => {
              const linkPassthroughProps = { ...linkProps };
              delete linkPassthroughProps.hideWhenSignedIn;
              return (
                <Fragment key={`${linkProps.children}-${i}`}>
                  {i > 0 && (
                    <Separator
                      orientation="vertical"
                      gap="sm"
                      className={CSS_CLASSES.GLOBAL_HEADER_UTILITY_LINK_SEPARATOR}
                    />
                  )}
                  <Link
                    bold={true}
                    color={variant === "dark" ? "white" : "gray"}
                    {...linkPassthroughProps}
                    className={classNames(CSS_CLASSES.GLOBAL_HEADER_UTILITY_LINK, linkProps.className)}
                  />
                </Fragment>
              );
            })}
          </div>
        )}
        {hasIconLinks && (
          <div className={CSS_CLASSES.GLOBAL_HEADER_UTILITY_ICON_LINKS}>
            {iconLinksToRender.map((iconLinkProps, i) => {
              const iconLinkPassthroughProps = { ...iconLinkProps };
              delete iconLinkPassthroughProps.placementOnMobile;
              delete iconLinkPassthroughProps.hideWhenSignedIn;
              return (
                <Fragment key={iconLinkProps.children.toString() || i}>
                  {!isDesktop && iconLinksToRender.length > 1 && i > 0 && variant === "light" && (
                    <Separator orientation="vertical" />
                  )}
                  <Link
                    bold={true}
                    color={variant === "dark" ? "white" : "black"}
                    {...iconLinkPassthroughProps}
                    className={classNames(CSS_CLASSES.GLOBAL_HEADER_UTILITY_ICON_LINK, iconLinkProps.className)}
                  />
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  ) : null;
};
